import axios from 'axios';
import { useCallback, useEffect, useState } from 'react';
import { unstable_serialize, useSWRConfig } from 'swr';

import * as voodooSdk from '@gmm/sdk-voodoo';
import { KlassDetail, KlassItem } from '@gmm/sdk/shared';
import { useCurrentTeacher } from '~/lib/hooks/useCurrentTeacher';
import { useSnackbar } from '~/lib/hooks/useSnackbar';

interface UpdatePracticeLockConfig {
  classId: string;
  manualPracticeLocked: boolean;
}

interface UseUpdatePracticeLock {
  isUpdating: boolean;
  updatePracticeLock: (config: UpdatePracticeLockConfig) => Promise<void>;
}

interface ClassCacheSnapshot {
  classList?: KlassItem[];
  classDetail?: KlassDetail;
  classDetailByTeacher?: KlassDetail;
}

const updatingPracticeLockClassIds = new Set<string>();
const updatingPracticeLockListenersByClassId = new Map<
  string,
  Set<() => void>
>();

// Lock updates can be triggered from multiple class-list controls, so the
// in-flight state lives outside any one rendered component instance.
const notifyPracticeLockListeners = (classId: string): void => {
  updatingPracticeLockListenersByClassId
    .get(classId)
    ?.forEach(listener => listener());
};

// Register the tiny callback each rendered control uses to hear about changes
// to this class's shared in-flight state.
const addPracticeLockListener = (
  classId: string,
  listener: () => void,
): void => {
  const classIdListeners =
    updatingPracticeLockListenersByClassId.get(classId) ?? new Set();

  classIdListeners.add(listener);
  updatingPracticeLockListenersByClassId.set(classId, classIdListeners);
};

// Remove the component callback when it unmounts, then drop the class entry once
// no rendered controls are listening for that class anymore.
const removePracticeLockListener = (
  classId: string,
  listener: () => void,
): void => {
  const classIdListeners = updatingPracticeLockListenersByClassId.get(classId);

  classIdListeners?.delete(listener);

  if (classIdListeners?.size === 0) {
    updatingPracticeLockListenersByClassId.delete(classId);
  }
};

// Change the shared in-flight Set first, then notify React subscribers so their
// disabled/loading state is based on the latest value.
const setPracticeLockUpdating = (
  classId: string,
  isUpdating: boolean,
): void => {
  if (isUpdating) {
    updatingPracticeLockClassIds.add(classId);
  } else {
    updatingPracticeLockClassIds.delete(classId);
  }

  notifyPracticeLockListeners(classId);
};

// Let one class-list row or status surface subscribe to the shared in-flight
// state for a single class id.
const useIsPracticeLockUpdating = (classId: string | undefined): boolean => {
  const [, setVersion] = useState(0);

  useEffect(() => {
    if (!classId) return;

    // Bump local state when this class changes so React re-reads the shared Set.
    const listener = (): void => setVersion(current => current + 1);

    addPracticeLockListener(classId, listener);

    return () => {
      removePracticeLockListener(classId, listener);
    };
  }, [classId]);

  return !!classId && updatingPracticeLockClassIds.has(classId);
};

// Let bulk controls watch a group of class ids without moving that shared state
// into a React context or store.
export const useArePracticeLocksUpdating = (
  classIds: readonly string[],
): boolean => {
  const [, setVersion] = useState(0);
  // Effects compare dependency values by identity, so the joined key lets this
  // hook resubscribe only when the actual watched class ids change.
  const classIdsKey = classIds.join(',');

  useEffect(() => {
    if (!classIdsKey) return;

    // Bulk controls watch several class ids but still use one small re-render signal.
    const listener = (): void => setVersion(current => current + 1);
    const subscribedClassIds = classIdsKey.split(',');

    subscribedClassIds.forEach(classId =>
      addPracticeLockListener(classId, listener),
    );

    return () => {
      subscribedClassIds.forEach(classId =>
        removePracticeLockListener(classId, listener),
      );
    };
  }, [classIdsKey]);

  return classIds.some(classId => updatingPracticeLockClassIds.has(classId));
};

export const useUpdatePracticeLock = (
  classIdForUpdatingState?: string,
): UseUpdatePracticeLock => {
  const { data: teacher } = useCurrentTeacher();
  const { enqueueGenericError } = useSnackbar({ autoTranslate: true });
  const { cache, mutate } = useSWRConfig();
  const isUpdating = useIsPracticeLockUpdating(classIdForUpdatingState);

  // This is the one write path for class practice lock: mark busy, update local
  // caches optimistically, call Voodoo, then rollback on failure.
  const updatePracticeLock = useCallback(
    async ({ classId, manualPracticeLocked }: UpdatePracticeLockConfig) => {
      // React state can lag behind a rapid second click, so the module-level
      // Set is the synchronous guard that prevents duplicate PATCH requests.
      if (!teacher || updatingPracticeLockClassIds.has(classId)) return;

      setPracticeLockUpdating(classId, true);

      // The UI updates first; these snapshots give the catch block enough state
      // to put every affected SWR cache back if the request fails.
      const previousClassCacheSnapshot = getClassCacheSnapshot(
        cache,
        teacher.id,
        classId,
      );
      const previousManualPracticeLocked = getCachedManualPracticeLocked(
        previousClassCacheSnapshot,
        classId,
      );

      try {
        await updateClassCaches(
          mutate,
          teacher.id,
          classId,
          manualPracticeLocked,
        );

        const config = voodooSdk.classes.updatePracticeLock(
          teacher.id,
          classId,
          { manualPracticeLocked },
        );

        await axios.request(config);
      } catch {
        if (typeof previousManualPracticeLocked === 'boolean') {
          await updateClassCaches(
            mutate,
            teacher.id,
            classId,
            previousManualPracticeLocked,
          );
        } else {
          await restoreClassCaches(
            mutate,
            teacher.id,
            classId,
            previousClassCacheSnapshot,
          );
        }

        enqueueGenericError();
      } finally {
        setPracticeLockUpdating(classId, false);
      }
    },
    [cache, enqueueGenericError, mutate, teacher],
  );

  return { isUpdating, updatePracticeLock };
};

// Write the same lock value into every SWR class cache shape the teacher UI may
// already have loaded.
const updateClassCaches = async (
  mutate: ReturnType<typeof useSWRConfig>['mutate'],
  teacherId: string,
  classId: string,
  manualPracticeLocked: boolean,
): Promise<void> => {
  // The sidebar and class detail views read different SWR keys, so update all
  // cached class shapes together to prevent a stale lock icon or status label.
  await Promise.all([
    mutate(
      ['classes.list', teacherId],
      updateClassList(classId, manualPracticeLocked),
      false,
    ),
    mutate(
      ['classes.show', teacherId, classId],
      updateClassDetail(classId, manualPracticeLocked),
      false,
    ),
    mutate(
      ['classes.show', classId],
      updateClassDetail(classId, manualPracticeLocked),
      false,
    ),
  ]);
};

// Put the SWR caches back exactly as they were when we cannot safely rollback
// from a single cached boolean value.
const restoreClassCaches = async (
  mutate: ReturnType<typeof useSWRConfig>['mutate'],
  teacherId: string,
  classId: string,
  snapshot: ClassCacheSnapshot,
): Promise<void> => {
  await Promise.all([
    mutate(['classes.list', teacherId], snapshot.classList, false),
    mutate(
      ['classes.show', teacherId, classId],
      snapshot.classDetailByTeacher,
      false,
    ),
    mutate(['classes.show', classId], snapshot.classDetail, false),
  ]);
};

// Read each relevant SWR cache entry before the optimistic update replaces it.
const getClassCacheSnapshot = (
  cache: ReturnType<typeof useSWRConfig>['cache'],
  teacherId: string,
  classId: string,
): ClassCacheSnapshot => ({
  classList: cache.get(unstable_serialize(['classes.list', teacherId])) as
    | KlassItem[]
    | undefined,
  classDetailByTeacher: cache.get(
    unstable_serialize(['classes.show', teacherId, classId]),
  ) as KlassDetail | undefined,
  classDetail: cache.get(unstable_serialize(['classes.show', classId])) as
    | KlassDetail
    | undefined,
});

// Find the best previous boolean across list and detail caches for a simple
// rollback that keeps newer cache object shapes intact.
const getCachedManualPracticeLocked = (
  { classList, classDetail, classDetailByTeacher }: ClassCacheSnapshot,
  classId: string,
): boolean | undefined => {
  // Prefer any concrete cached boolean so rollback preserves the last known
  // value instead of assuming a default when older cache entries are sparse.
  const classItem = classList?.find(
    ({ id, manualPracticeLocked }) =>
      id === classId && typeof manualPracticeLocked === 'boolean',
  );

  if (typeof classItem?.manualPracticeLocked === 'boolean') {
    return classItem.manualPracticeLocked;
  }

  return [classDetailByTeacher, classDetail].find(
    classDetail => typeof classDetail?.manualPracticeLocked === 'boolean',
  )?.manualPracticeLocked;
};

// Build the list updater once so SWR can map only the changed class item.
const updateClassItem =
  (classId: string, manualPracticeLocked: boolean) =>
  (item: KlassItem): KlassItem =>
    item.id === classId ? { ...item, manualPracticeLocked } : item;

// Return an SWR mutate callback for the class list cache.
const updateClassList =
  (classId: string, manualPracticeLocked: boolean) =>
  (classList: KlassItem[] | undefined): KlassItem[] | undefined =>
    classList?.map(updateClassItem(classId, manualPracticeLocked));

// Return an SWR mutate callback for either class detail cache key.
const updateClassDetail =
  (classId: string, manualPracticeLocked: boolean) =>
  (classDetail: KlassDetail | undefined): KlassDetail | undefined =>
    classDetail?.id === classId
      ? { ...classDetail, manualPracticeLocked }
      : classDetail;
